import { Inject, Injectable } from '@angular/core';
import { LaunchResult } from '@office-start/launcher-types';
import { logError, logFeature } from '@office-start/telemetry-service';
import { DataSecurityLevel as otelDataSecurityLevel, KnownErrorInstrumentationEvents, KnownFeatureNames } from '@office-start/telemetry-types';
import { Observable } from 'rxjs';
import { shareReplay, startWith } from 'rxjs/operators';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { FileType } from '../../shared/models/file-item.model';
import { Workload } from '../../shared/models/workload.model';
import { WrapperVersion } from '../../shared/models/wrapper-version.model';
import { MruData } from '../../shared/mru/mru-data.service';
import { ConsumerSkuType } from '../../shared/services/consumer-license.service';
import { Performance } from '../../shared/services/performance.service';
import { UpdateContentService } from '../../shared/services/update-content.service';
import { LaunchAction, UrlLauncherService } from '../../shared/services/url-launcher.service';
import { WRAPPER_VERSION } from '../../shared/services/wrapper-version.token';
import { CapabilitiesService } from '../../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../../shared/shared-context/consumer-install-config.service';
import { LocService } from '../../shared/shared-context/loc.service';
import { AppServiceCommand, AppServiceMethod } from '../models/app-service-command.model';
import { AppServiceResponseStatus } from '../models/app-service-response.model';
import { DiscoveredInstall, InstalledAppsExResponse } from '../models/installed-apps-ex-response.model';
import { InstalledApp, InstalledAppsResponse } from '../models/installed-apps-response.model';
import { InstalledOfficeDetailsResponse } from '../models/installed-office-details.model';
import { LocalDocument, LocalMruResponse } from '../models/local-mru-response.model';
import { OfficeLicensesResponse } from '../models/office-licenses.model';
import { AppServiceConnection } from './app-service-connection.service';
import { PwaConfigService } from './pwa-config.service';

interface ObservableWithSuccess<T> {
    observable: Observable<T>;
    hadSuccess?: boolean;
}

interface InstalledApps {
    apps: Array<LocalAppInfo>;
}

interface LocalAppInfo {
    name: string;
    identifier: number;
    isInstalled: boolean;
    aumid?: string;
    command?: string;
    uri?: string;
    version?: string;
    o365Installed?: DiscoveredInstall;
    perpetualInstalled?: DiscoveredInstall;
    launchInstall?: DiscoveredInstall;
}

const enum PwaNativeBridgeInstrumentationState {
    ApiNotExist = 'ApiNotExist',
    DetailsFailed = 'DetailsFailed',
    Failed = 'Failed',
    FailedEx = 'FailedEx',
    FailedInitial = 'FailedInitial',
    NoSubscription = 'NoSubscription',
    NotInstalled = 'NotInstalled',
    NotModern = 'NotModern',
    SubscriptionFetchFailed = 'SubscriptionFetchFailed',
    Success = 'Success'
}

// Service that PWA use to retrieve local MRU documents via native bridge.
@Injectable({
    providedIn: 'root'
})
export class PwaNativeBridgeService {

    private installedlocalAppsPromise: Promise<InstalledApps> = null;
    private installedLocalAppsExPromise: Promise<InstalledApps> = null;
    private installedOfficeDetailsPromise: Promise<InstalledOfficeDetailsResponse> = null;
    private officeLicensePromise: Promise<OfficeLicensesResponse> = null;
    private consumerSubscriptionStatusPromise: Promise<ConsumerSkuType> = null;
    private localDocsRequestIndex = 0;
    private readonly isMsa: boolean;
    private readonly recentFilesObservables = new Map<string, ObservableWithSuccess<MruData>>();

    public static readonly launchProtocol = 'ms-officecmd:';

    constructor(
        private readonly appServiceConnection: AppServiceConnection,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly instrumentationService: InstrumentationService,
        private readonly locService: LocService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly pwaConfigService: PwaConfigService,
        @Inject(WRAPPER_VERSION) private readonly wrapperVersion: WrapperVersion,
        private readonly updateContentService: UpdateContentService
    ) {
        if (this.capabilitiesService.capabilities.pwaEnabled) {
            this.isMsa = this.clientStartupConfigService.config.authType === AuthType.MSA;
            this.getInstalledAppsEx(true);
            this.getConsumerSubscriptionStatus();
        }
    }

    public getLocalMru(types?: Set<FileType>): Observable<MruData> {
        const appsString = types ? Array.from(types).map(t => FileType[t]).join(',') : '';
        const updateKey = `localMru-${appsString}`;
        if (!this.recentFilesObservables.has(appsString)) {
            const updateObservable = this.updateContentService.getOrAddUpdateStream(updateKey, () => this.getLocalMruPromise(appsString), undefined, undefined, undefined, true);
            const localObservable: ObservableWithSuccess<MruData> = {observable: updateObservable.pipe(startWith<MruData>({ items: [], impressionId: 'LocalMruImpression', fromCache: false }), shareReplay(1))};
            this.recentFilesObservables.set(appsString, localObservable);
        }

        const mruObservable = this.recentFilesObservables.get(appsString);

        if (!mruObservable.hadSuccess) {
            this.updateContentService.pushUpdate(updateKey);
        }

        return mruObservable.observable;
    }

    public filterUninstalledApps(appsToLookFor: Array<Workload>): Promise<Array<Workload>> {
        return new Promise(resolve => {
            if (appsToLookFor && appsToLookFor.length > 0) {
                const allInstalledAppsPromises = appsToLookFor.map(workload => {
                    const filterApp = () => appsToLookFor = appsToLookFor.filter(workloadData => workloadData.Id !== workload.Id);
                    return this.getInstalledApp(workload.Title).then((appInfo: LocalAppInfo) => {
                        if (!appInfo) {
                            filterApp();
                        }
                    }, filterApp);
                });
                Promise.all(allInstalledAppsPromises).then(() => {
                    resolve(appsToLookFor);
                });
            } else {
                resolve(appsToLookFor);
            }
        });
    }

    public launchOfficeApp(appName, filePath, launchLocalAppStart): Promise<boolean> {
        return new Promise( resolve => {
            this.getInstalledApp(appName).then(appInfo => {
                if (!appInfo) {
                    this.handleError(KnownInstrumentationEvents.LaunchLocalApp, 'Launch_Local_App', PwaNativeBridgeInstrumentationState.NotInstalled, `${appName} not installed`);
                    resolve(false);
                    return;
                }

                const details: InstalledApp = {
                    'appId': appInfo.identifier,
                    'name': appInfo.name,
                    'discovered': {}
                };

                for (const prop of ['command', 'uri', 'aumid']) {
                    if (appInfo.launchInstall) {
                        if (appInfo.launchInstall.hasOwnProperty(prop)) {
                            details.discovered[prop] = appInfo.launchInstall[prop];
                        }
                    } else {
                        // If ex app launch info doesn't exist, will try to get the old luanch info.
                        if (appInfo.hasOwnProperty(prop)) {
                            details.discovered[prop] = appInfo[prop];
                        }
                    }
                }

                const commands: Array<AppServiceCommand> = [
                    { method: AppServiceMethod.Id, data: 3 },
                    {
                        method: AppServiceMethod.LaunchApp,
                        data: {
                            details,
                            filename: filePath
                        }
                    }
                ];

                this.launchFromBridge(commands, launchLocalAppStart).then(() => {
                    resolve(true);
                }, () => {
                    resolve(false);
                });
            }).catch(reason => {
                this.handleError(KnownInstrumentationEvents.LaunchLocalApp, 'Launch_Local_App', PwaNativeBridgeInstrumentationState.Failed, reason);
                resolve(false);
            });
        });
    }

    public getInstalledApp(appName: string): Promise<LocalAppInfo> {
        const appNameLowerCase = appName.toLowerCase();
        return new Promise((resolve, reject) => {
            this.getInstalledAppsEx().then(installedAppsEx => {
                if (!installedAppsEx) {
                    throw new Error('getInstalledAppsEx returned null');
                }

                const appInfo = installedAppsEx.apps.find(item => item.name.toLowerCase() === appNameLowerCase);
                if (!appInfo || !appInfo.isInstalled) {
                    this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.NotInstalled, appName);
                    resolve(null);
                    return;
                }

                // For non-core apps or AAD users, we will launch app if it's installed.
                if (!this.isCoreApp(appName) || !this.isMsa) {
                    appInfo.launchInstall = appInfo.o365Installed || appInfo.perpetualInstalled;
                    if (appInfo.launchInstall) {
                        resolve(appInfo);
                    } else {
                        this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.NotModern, appName);
                        resolve(null);
                    }
                    return;
                }

                // For core apps and MSA users
                if (appInfo.perpetualInstalled) {
                    appInfo.launchInstall = appInfo.perpetualInstalled;
                    resolve(appInfo);
                } else if (appInfo.o365Installed) {
                    this.getCurrentOfficeLicenses().then(officeLicenses => {
                        if (this.hasLicensedInstall(officeLicenses)) {
                            appInfo.launchInstall = appInfo.o365Installed;
                            resolve(appInfo);
                            return;
                        }
                        this.updateAppInfoBySubscriptionStatus(appName, appInfo).then(resolve);
                    }).catch(() => {
                        // If we cannot detect the license info, we will send paid MSA users to client and free MSA users to web.
                        this.updateAppInfoBySubscriptionStatus(appName, appInfo).then(resolve);
                    });
                } else {
                    this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.NotInstalled, appName);
                    resolve(null);
                }
            }).catch(getInstalledAppsExError => {
                this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.FailedEx, getInstalledAppsExError);
                if (getInstalledAppsExError.toString().indexOf(PwaNativeBridgeInstrumentationState.ApiNotExist) !== -1) {
                    // Try getInstalledApps() if getInstalledAppsEx() API doesn't exist.
                    // TODO: The following code should be removed once we have most UWP clients are running the getInstalledAppsEx() native bridge.
                    this.getInstalledApps().then(installedApps => {
                        if (!installedApps) {
                            throw new Error('Installed apps returned null');
                        }
                        const appInfo = installedApps.apps.find(item => item.name.toLowerCase() === appNameLowerCase);
                        if (!appInfo || !appInfo.isInstalled) {
                            this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.NotInstalled, appName);
                            resolve(null);
                            return;
                        }

                        if (!this.isCoreApp(appInfo.name)) {
                            resolve(appInfo);
                            return;
                        }

                        // For O365, check if the user has an active subscription.
                        if (!this.isMsa) {
                            resolve(appInfo);
                        } else {
                            // Get installed Office details to check if it's O365
                            this.getOfficeInstallationDetails().then(installedOfficeDetails => {
                                if (!installedOfficeDetails.IsO365) {
                                    resolve(appInfo);
                                    return;
                                }
                                this.updateAppInfoBySubscriptionStatus(appName, appInfo).then(resolve);
                            }).catch(getOfficeDetailsError => {
                                this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.DetailsFailed, getOfficeDetailsError);
                                // Users might hit error if they have the old version of UWP which hasn't white list this bridge call yet.
                                // We will launch the app since they have modern version installed.
                                resolve(appInfo);
                                return;
                            });
                        }
                    }).catch(getInstalledAppsError => {
                        this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.Failed, getInstalledAppsError);
                        reject('getInstalledApps failed: ' + getInstalledAppsError);
                    });
                } else {
                    resolve(null);
                }
            });
        });
    }

    private static isValidDocument(doc: LocalDocument) {
        return doc.name && doc.location && doc.lastAccessedDate && doc.applicationName;
    }

    private getOfficeInstallationDetails(): Promise<InstalledOfficeDetailsResponse> {
        if (!this.installedOfficeDetailsPromise) {
            this.installedOfficeDetailsPromise = new Promise((resolve, reject) => {
                // New api was added in 1902 (Feb 2019). New uwp builds roll out slowly and old builds have been seen to throw null ref errors
                if (this.wrapperVersion && this.wrapperVersion.major === 18 && this.wrapperVersion.minor < 1902) {
                    this.handleError(KnownInstrumentationEvents.InstalledOfficeDetailsFetch, 'Installed_Office_Details_Fetch', PwaNativeBridgeInstrumentationState.ApiNotExist, '', 0);
                    reject(`getOfficeInstallationDetails failed: ${PwaNativeBridgeInstrumentationState.ApiNotExist}`);
                    return;
                }

                const newCommands: Array<AppServiceCommand> = [
                    { method: AppServiceMethod.Id, data: 4 },
                    { method: AppServiceMethod.GetOfficeInstallationDetails, data: null }
                ];
                const commandStart = Performance.now();
                let commandLatency: number;
                this.appServiceConnection.sendCommands(newCommands).then( response => {
                    commandLatency = this.calculateLatency(commandStart);
                    if (response.status !== AppServiceResponseStatus.Success) {
                        throw new Error('AppService sendMessageAsync returned status: ' + response.status);
                    }

                    // TODO: We see some parsing error in logs, logic below ensures the response strings are logged so they can be investigated
                    let detailsFromResponse: InstalledOfficeDetailsResponse;
                    try {
                        detailsFromResponse = JSON.parse(response.message[AppServiceMethod.GetOfficeInstallationDetails]);
                    } catch (e) {
                        throw new Error(`InstalledOfficeDetailsResponse JSON parsing failed. r.msg: ${!response.message}, InstalledOfficeDetailsResponse: ${!(response.message && !response.message[AppServiceMethod.GetOfficeInstallationDetails])}`);
                    }

                    resolve(detailsFromResponse);
                    const params = {};
                    params[InstrumentationParam.State] = PwaNativeBridgeInstrumentationState.Success;
                    params[InstrumentationParam.Latency] = commandLatency.toString();
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.InstalledOfficeDetailsFetch, params, [], DataSecurityLevel.Enhanced);
                    logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.InstalledOfficeDetailsFetch, params}, otelDataSecurityLevel.Enhanced);
                }).catch(reason => {
                    commandLatency = commandLatency || this.calculateLatency(commandStart);
                    this.installedOfficeDetailsPromise = null;
                    this.handleError(KnownInstrumentationEvents.InstalledOfficeDetailsFetch, 'Installed_Office_Details_Fetch', PwaNativeBridgeInstrumentationState.Failed, reason, commandLatency);
                    reject('getOfficeInstallationDetails failed: ' + reason);
                });
            });
        }

        return this.installedOfficeDetailsPromise;
    }

    private getCurrentOfficeLicenses(): Promise<OfficeLicensesResponse> {
        if (!this.officeLicensePromise) {
            this.officeLicensePromise = new Promise((resolve, reject) => {
                const newCommands: Array<AppServiceCommand> = [
                    { method: AppServiceMethod.Id, data: 6 },
                    { method: AppServiceMethod.GetCurrentOfficeLicenses, data: null }
                ];
                const commandStart = Performance.now();
                let commandLatency: number;
                this.appServiceConnection.sendCommands(newCommands).then(response => {
                    commandLatency = this.calculateLatency(commandStart);
                    if (response.status !== AppServiceResponseStatus.Success) {
                        throw new Error('AppService sendMessageAsync returned status: ' + response.status);
                    }

                    let licensesFromResponse: OfficeLicensesResponse;
                    try {
                        licensesFromResponse = JSON.parse(response.message[AppServiceMethod.GetCurrentOfficeLicenses]);
                    } catch (e) {
                        throw new Error(`OfficeLicensesResponse JSON parsing failed. r.msg: ${!response.message}, OfficeLicensesResponse: ${!(response.message && !response.message[AppServiceMethod.GetCurrentOfficeLicenses])}`);
                    }

                    if (licensesFromResponse) {
                        licensesFromResponse.forEach(license => license.LicenseStatus = license.LicenseStatus.toLowerCase());
                    }

                    resolve(licensesFromResponse);
                    const params = {};
                    params[InstrumentationParam.State] = PwaNativeBridgeInstrumentationState.Success;
                    params[InstrumentationParam.Latency] = commandLatency.toString();
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.OfficeLicensesFetch, params, [], DataSecurityLevel.Enhanced);
                    logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.OfficeLicensesFetch, params }, otelDataSecurityLevel.Enhanced);
                }).catch(reason => {
                    commandLatency = commandLatency || this.calculateLatency(commandStart);
                    this.officeLicensePromise = null;
                    this.handleError(KnownInstrumentationEvents.OfficeLicensesFetch, 'Office_Licenses_Fetch', PwaNativeBridgeInstrumentationState.Failed, reason, commandLatency);
                    reject('getCurrentOfficeLicenses failed: ' + reason);
                });
            });
        }

        return this.officeLicensePromise;
    }

    private getInstalledAppsEx(initialFetch?: boolean): Promise<InstalledApps> {
        if (!this.installedLocalAppsExPromise) {
            this.installedLocalAppsExPromise = new Promise((resolve, reject) => {
                // New api was added in 1903 (March 2019). New uwp builds roll out slowly and old builds have been seen to throw null ref errors
                if (this.wrapperVersion && this.wrapperVersion.major === 18 && this.wrapperVersion.minor < 1903) {
                    this.handleError(KnownInstrumentationEvents.InstalledAppsExFetch, 'Installed_Apps_Ex_Fetch', PwaNativeBridgeInstrumentationState.ApiNotExist, '', 0);
                    reject(`getInstalledAppsEx failed: ${PwaNativeBridgeInstrumentationState.ApiNotExist}`);
                    return;
                }

                const apps = {
                    apps: this.pwaConfigService.config.installedAppsToLookFor.split(',')
                };

                const commands: Array<AppServiceCommand> = [
                    { method: AppServiceMethod.Id, data: 5 },
                    {
                        method: AppServiceMethod.GetInstalledAppsEx,
                        data: {
                            version: 1,
                            apps
                        }
                    }
                ];
                const commandStart = Performance.now();
                let commandLatency: number;
                this.appServiceConnection.sendCommands(commands).then(response => {
                    commandLatency = this.calculateLatency(commandStart);
                    if (response.status !== AppServiceResponseStatus.Success) {
                        throw new Error('AppService sendMessageAsync returned status: ' + response.status);
                    }

                    // TODO: We see some parsing error in logs, logic below ensures the response strings are logged so they can be investigated
                    let appsFromResponse: InstalledAppsExResponse;
                    try {
                        appsFromResponse = JSON.parse(response.message[AppServiceMethod.GetInstalledAppsEx]);
                    } catch (e) {
                        if (response.message && response.message[AppServiceMethod.GetInstalledAppsEx]) {
                            if (response.message[AppServiceMethod.GetInstalledAppsEx].toString().indexOf('forbidden') !== -1) {
                                throw new Error(PwaNativeBridgeInstrumentationState.ApiNotExist);
                            } else {
                                this.logToDebugCache(KnownInstrumentationEvents.InstalledAppsExFetchDebug, PwaNativeBridgeInstrumentationState.FailedEx, response.message[AppServiceMethod.GetInstalledAppsEx].toString());
                            }
                        }

                        throw new Error(`Apps JSON parsing failed. r.msg: ${!response.message}, appsFromResponse: ${!(response.message && !response.message[AppServiceMethod.GetInstalledAppsEx])}`);
                    }

                    resolve({
                        apps: appsFromResponse.map(x => {
                            const app: LocalAppInfo = {
                                name: x.name,
                                identifier: x.appId,
                                isInstalled: x.hasOwnProperty('discovered') && x.discovered.length > 0
                            };

                            if (x.discovered && x.discovered.length > 0) {
                                x.discovered.forEach(discoveredInstall => {
                                    if (discoveredInstall.isO365) {
                                        if (!app.hasOwnProperty('o365Installed') || this.getMajorVersion(app.o365Installed.version) < this.getMajorVersion(discoveredInstall.version)) {
                                            app.o365Installed = discoveredInstall;
                                        }
                                    } else {
                                        if (!app.hasOwnProperty('perpetualInstalled') || this.getMajorVersion(app.perpetualInstalled.version) < this.getMajorVersion(discoveredInstall.version)) {
                                            app.perpetualInstalled = discoveredInstall;
                                        }
                                    }
                                });
                            }
                            return app;
                        })
                    });

                    const params = {};
                    params[InstrumentationParam.State] = PwaNativeBridgeInstrumentationState.Success;
                    params[InstrumentationParam.Latency] = commandLatency.toString();
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.InstalledAppsExFetch, params, [], DataSecurityLevel.Enhanced);
                    logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.InstalledAppsExFetch, params }, otelDataSecurityLevel.Enhanced);
                }).catch(reason => {
                    commandLatency = commandLatency || this.calculateLatency(commandStart);
                    if (reason.message === PwaNativeBridgeInstrumentationState.ApiNotExist) {
                        this.handleError(KnownInstrumentationEvents.InstalledAppsExFetch, 'Installed_Apps_Ex_Fetch', PwaNativeBridgeInstrumentationState.ApiNotExist, '', commandLatency);
                    } else {
                        const state = initialFetch ? PwaNativeBridgeInstrumentationState.FailedInitial : PwaNativeBridgeInstrumentationState.Failed;
                        this.handleError(KnownInstrumentationEvents.InstalledAppsExFetch, 'Installed_Apps_Ex_Fetch', state, reason, commandLatency);
                    }
                    this.installedLocalAppsExPromise = null;
                    reject('getInstalledAppsEx failed: ' + reason);
                });
            });
        }

        return this.installedLocalAppsExPromise;
    }

    private getInstalledApps(initialFetch?: boolean): Promise<InstalledApps> {
        if (!this.installedlocalAppsPromise) {
            this.installedlocalAppsPromise = new Promise((resolve, reject) => {
                const apps = {
                    apps: this.pwaConfigService.config.installedAppsToLookFor.split(',')
                };

                const commands: Array<AppServiceCommand> = [
                    { method: AppServiceMethod.Id, data: 2 },
                    { method: AppServiceMethod.GetInstalledApps, data: apps }
                ];
                const commandStart = Performance.now();
                let commandLatency: number;
                this.appServiceConnection.sendCommands(commands).then(response => {
                    commandLatency = this.calculateLatency(commandStart);
                    if (response.status !== AppServiceResponseStatus.Success) {
                        throw new Error('AppService sendMessageAsync returned status: ' + response.status);
                    }
                    // TODO: We see some parsing error in logs, logic below ensures the response strings are logged so they can be investigated
                    let appsFromResponse: InstalledAppsResponse;
                    try {
                        appsFromResponse = JSON.parse(response.message[AppServiceMethod.GetInstalledApps]);
                    } catch (e) {
                        throw new Error(`Apps JSON parsing failed. r.msg: ${!response.message}, appsFromResponse: ${!(response.message && !response.message[AppServiceMethod.GetInstalledApps])}`);
                    }

                    resolve({
                        apps: appsFromResponse.map(x => {
                            const app: LocalAppInfo = {
                                name: x.name,
                                identifier: x.appId,
                                isInstalled: x.hasOwnProperty('discovered')
                            };

                            if (app.isInstalled && x.discovered) {
                                for (const prop of ['command', 'uri', 'aumid', 'version']) {
                                    if (x.discovered.hasOwnProperty(prop)) {
                                        app[prop] = x.discovered[prop];
                                    }
                                }
                            }

                            return app;
                        })
                    });

                    const params = {};
                    params[InstrumentationParam.State] = PwaNativeBridgeInstrumentationState.Success;
                    params[InstrumentationParam.Latency] = commandLatency.toString();
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.InstalledAppsFetch, params, [], DataSecurityLevel.Enhanced);
                    logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.InstalledAppsFetch, params}, otelDataSecurityLevel.Enhanced);
                }).catch(reason => {
                    commandLatency = commandLatency || this.calculateLatency(commandStart);
                    const state: string = initialFetch ? PwaNativeBridgeInstrumentationState.FailedInitial : PwaNativeBridgeInstrumentationState.Failed;
                    this.handleError(KnownInstrumentationEvents.InstalledAppsFetch, 'Installed_Apps_Fetch', state, reason, commandLatency);
                    this.installedlocalAppsPromise = null;
                    reject('getInstalledApps failed: ' + reason);
                });
            });
        }

        return this.installedlocalAppsPromise;
    }

    private logToDebugCache(eventName: KnownInstrumentationEvents, state: string, error: any) {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.State] = state;
        params[InstrumentationParam.Error] = (error || '').toString();
        this.instrumentationService.logDebugCacheEvent(eventName, params, [], DataSecurityLevel.Enhanced);
    }

    private handleError(eventName: KnownInstrumentationEvents, otelEventName: KnownErrorInstrumentationEvents, state: string, error: any, latency?: number) {
        const params = {};
        params[InstrumentationParam.State] = state;
        params[InstrumentationParam.Error] = (error || '').toString();
        if (typeof latency === 'number') {
            params[InstrumentationParam.Latency] = latency.toString();
        }
        this.instrumentationService.logGenericEvent(eventName, params, [], DataSecurityLevel.Enhanced);
        logError({eventName: otelEventName, message: (error || '').toString()}, otelDataSecurityLevel.Enhanced);
        logFeature({ featureName: KnownFeatureNames.Hwa, eventName: otelEventName, params}, otelDataSecurityLevel.Enhanced);
    }

    private launchFromBridge(commands: Array<AppServiceCommand>, launchLocalAppStart: number): Promise<void> {
        const commandStart = Performance.now();
        // Calculate the exact startTime to pass to Protocol request
        const unixStart = Math.ceil(Date.now() - (commandStart - launchLocalAppStart)).toString();
        // If the protocol uri is present use it because it will launch the app in the foreground
        return this.launchViaProtocol(commands, unixStart).then((result: boolean | void | LaunchResult) => {
            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                const launchResult = result as LaunchResult;
                if ((launchResult && !launchResult.success) || !result) {
                    return this.handleLaunchViaProtocolError(commands, commandStart, launchLocalAppStart, 'false' /* error */);
                }
            }

            const params = {};
            params[InstrumentationParam.State] = PwaNativeBridgeInstrumentationState.Success;
            params[InstrumentationParam.Action] = 'Protocol';
            params[InstrumentationParam.Latency] = this.calculateLatency(commandStart).toString();
            params[InstrumentationParam.DebugInfo] = `StartLatency:${this.calculateLatency(launchLocalAppStart)}`;
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.LaunchLocalApp, params, [], DataSecurityLevel.Enhanced);
            logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.LaunchLocalApp, params}, otelDataSecurityLevel.Enhanced);

        }, error =>
            this.handleLaunchViaProtocolError(commands, commandStart, launchLocalAppStart, error)
        );
    }

    private handleLaunchViaProtocolError(commands: Array<AppServiceCommand>, failedCommandStart: number, launchLocalAppStart: number, error: string): Promise<void> {
        const errorParams = {};
            errorParams[InstrumentationParam.Error] = error;
            errorParams[InstrumentationParam.Latency] = this.calculateLatency(failedCommandStart).toString();
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.LaunchByProtocolFailed, errorParams, [], DataSecurityLevel.Enhanced);
            logFeature({ featureName: KnownFeatureNames.Hwa, eventName: 'Launch_By_Protocol_Failed', params: errorParams }, otelDataSecurityLevel.Enhanced);
            const commandStart = Performance.now();
            let commandLatency: number;
            return this.appServiceConnection.sendCommands(commands).then( response => {
                commandLatency = this.calculateLatency(commandStart);
                if (response.status !== AppServiceResponseStatus.Success) {
                    return Promise.reject('AppService sendMessageAsync returned status - ' + response.status);
                }

                if (!response.message) {
                    return Promise.reject('AppService response message missing');
                }

                const result = response.message[AppServiceMethod.LaunchApp];

                if (!result) {
                    return Promise.reject('AppService message result missing');
                }

                if (typeof(result) === 'number' && result <= 32) {
                    return Promise.reject('AppService message returned error result - ' + result);
                }

                const params = {};
                params[InstrumentationParam.State] = PwaNativeBridgeInstrumentationState.Success;
                params[InstrumentationParam.Action] = 'Bridge';
                params[InstrumentationParam.Latency] = commandLatency.toString();
                params[InstrumentationParam.DebugInfo] = `StartLatency:${this.calculateLatency(launchLocalAppStart)}`;
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.LaunchLocalApp, params, [], DataSecurityLevel.Enhanced);
                logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.LaunchLocalApp, params}, otelDataSecurityLevel.Enhanced);
            }).catch( reason => {
                commandLatency = commandLatency || this.calculateLatency(commandStart);
                this.handleError(KnownInstrumentationEvents.LaunchLocalApp, 'Launch_Local_App', PwaNativeBridgeInstrumentationState.Failed, reason, commandLatency);
                return Promise.reject();
            });
    }

    private getOneDriveLaunchUri(): string {
        let uri = 'odopen://launch';
        if (this.clientStartupConfigService.config.authType === AuthType.OrgId) {
            const account = this.clientStartupConfigService.config.userPrincipalName;
            uri += `?accounttype=business&userEmail=${account}`;
        } else {
            uri += '?accounttype=personal';
        }

        return uri;
    }

    private launchViaProtocol(commands: Array<AppServiceCommand>, launchLocalAppStart: string): Promise<boolean | void | LaunchResult> {
        return this.urlLauncherService.supportsProtocol(PwaNativeBridgeService.launchProtocol).then( supported => {
            if (!supported) {
                return Promise.reject('Launcher unavailable');
            }
            let launchUri = '';
            const commandDataDetails = commands[1].data.details;
            if (commandDataDetails && commandDataDetails.name === 'OneDrive' &&
                commandDataDetails.discovered && commandDataDetails.discovered.uri &&
                commandDataDetails.discovered.uri.toLowerCase() === 'odopen') {
                launchUri = this.getOneDriveLaunchUri();
            } else {
                const message = {};
                commands.forEach(command => {
                    message[command.method] = command.data;
                });

                const clientStartupContext = this.clientStartupConfigService.config.sharedClientStartupContext;
                const cid = clientStartupContext.correlationId;
                const sid = clientStartupContext.sessionId;
                // Message is stringified twice so that parameter is sent as string
                launchUri = PwaNativeBridgeService.launchProtocol + JSON.stringify(JSON.stringify(message)) + ' cid:' + cid + ' sid:' + sid + ' requestStart:' + launchLocalAppStart;
            }

            if (this.capabilitiesService.capabilities.contentLauncherEnabled) {
                return import('@office-start/launcher-service').then(
                    launchSvc =>
                        launchSvc.launchUrl(launchUri, {action: 'LaunchProtocol'})
                );
            } else {
                return this.urlLauncherService.openUrl(launchUri, LaunchAction.LaunchProtocol);
            }
        });
    }

    private getLocalMruPromise(appsString: string): Promise<MruData> {
        return new Promise<MruData>((resolve, reject) => {
            const apps = {
                apps: appsString
            };

            const commands: Array<AppServiceCommand> = [
                { method: AppServiceMethod.Id, data: 1 },
                { method: AppServiceMethod.GetLocalMru, data: apps }
            ];
            const commandStart = Performance.now();
            let commandLatency: number;
            this.appServiceConnection.sendCommands(commands).then(response => {
                commandLatency = this.calculateLatency(commandStart);
                if (response.status !== AppServiceResponseStatus.Success) {
                    throw new Error('Error in response: ' + response.status);
                }

                const mruResponse: LocalMruResponse = JSON.parse(response.message[AppServiceMethod.GetLocalMru]);
                let mru = mruResponse.mru.filter(PwaNativeBridgeService.isValidDocument);
                const buildMruData = () => {
                    resolve({
                        fromCache: false,
                        impressionId: 'LocalMruImpression',
                        items: mru.map(x => {
                            const path = x.location.split('\\');
                            const folder = path.length > 1 ? path[path.length - 2] : '';
                            const lastAccessed = new Date(x.lastAccessedDate);
                            const application = x.applicationName.charAt(0).toUpperCase() + x.applicationName.substring(1);
                            const name = x.name.replace(/\0/gi, '');
                            return {
                                DocId: '',
                                DriveId: '',
                                FileName: name.trim(),
                                Title: name.lastIndexOf('.') === -1 ? name : name.substring(0, name.lastIndexOf('.')),
                                Url: x.location,
                                WebUrl: '',
                                LastAccessedTime: lastAccessed,
                                DisplayLastAccessedTime: this.locService.localizedDateString(lastAccessed),
                                FileType: x.applicationName,
                                Provenance: 'OfficeMru',
                                PathArray: [folder],
                                IsPinned: false,
                                Application: application,
                                IsLocal: true
                            };
                        })
                    });

                    if (this.recentFilesObservables.has(appsString)) {
                        this.recentFilesObservables.get(appsString).hadSuccess = true;
                    }

                    const params = {};
                    params[InstrumentationParam.State] = PwaNativeBridgeInstrumentationState.Success;
                    params[InstrumentationParam.Index] = (this.localDocsRequestIndex++).toString();
                    params[InstrumentationParam.Latency] = commandLatency.toString();
                    this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.LocalMruFetch, params, [], DataSecurityLevel.Enhanced);
                    logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.LocalMruFetch, params }, otelDataSecurityLevel.Enhanced);
                };
                const filterOneNoteMru = () => mru = mru.filter(x => x.applicationName.toLowerCase() !== FileType[FileType.OneNote].toLowerCase());
                return this.getInstalledApp(FileType[FileType.OneNote]).then((appInfo: LocalAppInfo) => {
                    if (!appInfo) {
                        filterOneNoteMru();
                    }
                    buildMruData();
                }, () => {
                    filterOneNoteMru();
                    buildMruData();
                });
            }).catch(reason => {
                commandLatency = commandLatency || this.calculateLatency(commandStart);
                const params = {};
                params[InstrumentationParam.State] = PwaNativeBridgeInstrumentationState.Failed;
                params[InstrumentationParam.Error] = 'getLocalMru failed: ' + reason;
                params[InstrumentationParam.Index] = (this.localDocsRequestIndex++).toString();
                params[InstrumentationParam.Latency] = commandLatency.toString();
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.LocalMruFetch, params, [], DataSecurityLevel.Enhanced);
                logFeature({ featureName: KnownFeatureNames.Hwa, eventName: KnownInstrumentationEvents.LocalMruFetch, params }, otelDataSecurityLevel.Enhanced);
                reject('Local MRU failed');
            });
        });
    }

    private async updateAppInfoBySubscriptionStatus(appName: string, appInfo: LocalAppInfo): Promise<LocalAppInfo> {
        return this.getConsumerSubscriptionStatus().then(skuType => {
            if (this.consumerHasSubscriptionOrPerpetual(skuType)) {
                appInfo.launchInstall = appInfo.o365Installed;
                return appInfo;
            }

            this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.NoSubscription, appName);
            return null;
        });
    }

    private getConsumerSubscriptionStatus(): Promise<ConsumerSkuType> {
        if (!this.consumerSubscriptionStatusPromise) {
            this.consumerSubscriptionStatusPromise = new Promise(resolve => {
                if (this.isMsa) {
                    this.consumerInstallConfigService.getConfig().then(config => {
                        const subscriptionStatus = config && config.currentSkuInfo ? config.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
                        resolve(subscriptionStatus);
                    }).catch(reason => {
                        this.handleError(KnownInstrumentationEvents.AppInstalledStatus, 'App_Installed_Status', PwaNativeBridgeInstrumentationState.SubscriptionFetchFailed, reason);
                        resolve(ConsumerSkuType.NotDetected);
                    });
                } else {
                    resolve(null);
                }
            });
        }

        return this.consumerSubscriptionStatusPromise;
    }

    private hasLicensedInstall(licenses: OfficeLicensesResponse): boolean {
        if (this.pwaConfigService.config.rfmToWeb) {
            const validStatus = this.pwaConfigService.config.oobToWeb ? ['licensed', 'ootgrace', 'extendedgrace'] : ['licensed', 'ootgrace', 'extendedgrace', 'oobgrace'];
            return !!licenses && licenses.some(x => validStatus.indexOf(x.LicenseStatus.toLowerCase()) !== -1);
        } else {
            return !!licenses && licenses.some(x => (x.LicenseStatus.toLowerCase() !== 'unlicensed' && x.LicenseStatus.toLowerCase() !== 'unknown'));
        }
    }

    private getMajorVersion(version: string): number {
        return parseInt(version.substring(0, 2)) || 0;
    }

    private isCoreApp(appName: string): boolean {
        const appsNeedModern = ['outlook', 'word', 'excel', 'powerpoint'];
        return appsNeedModern.indexOf(appName.toLowerCase()) !== -1;
    }

    private consumerHasSubscriptionOrPerpetual(skuType: ConsumerSkuType): boolean {
        return skuType !== ConsumerSkuType.NotSubscribed && skuType !== ConsumerSkuType.NonActive;
    }

    private calculateLatency(startTime: number): number {
        return Math.ceil(Performance.now() - startTime);
    }
}
